import { onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import CardLayer from "./components/CardLayer";
import ChatLayer from "./components/ChatLayer";
import CitySelector from "./components/CitySelector";
import FloatingButtons from "./components/FloatingButtons";
import InfoCard from "./components/InfoCard";
import { auth, functions } from "./firebase";
import { FlowController, INTERACTION_TYPES, SCREENS, classifyVoiceIntent } from "./FlowController";

// Sleep utility function
const sleep = (seconds) =>
  new Promise((resolve) => setTimeout(resolve, seconds * 1000));

function App() {
  // Authentication state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginInProgress, setLoginInProgress] = useState(false);
  const [logoutInProgress, setLogoutInProgress] = useState(false);
  // Removed showDeleteDataConfirm state - no longer needed
  const [showNewReadingButton, setShowNewReadingButton] = useState(false);
  const [hasReachedTopics, setHasReachedTopics] = useState(false);
  const [voiceMode, setVoiceModeState] = useState(false);
  const [voiceStreamText, setVoiceStreamText] = useState("");
  const [voiceStreamCursor, setVoiceStreamCursor] = useState(false);
  const [voiceSubtitleFading, setVoiceSubtitleFading] = useState(false);
  const [woojuSpeaking, setWoojuSpeaking] = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const voiceModeRef = useRef(false);

  // State for card content
  const [cardString, setCardString] = useState("");
  const [cardReadingText, setCardReadingText] = useState("");
  const [infoCardResetKey, setInfoCardResetKey] = useState(0);
  const [citySelectorResetKey, setCitySelectorResetKey] = useState(0);
  const [isPartnerForm, setIsPartnerForm] = useState(false);

  // Refs for the four main layers
  const infoCardLayer = useRef(null);
  const citySelectorLayer = useRef(null);
  const woojuMachineLayer = useRef(null);
  const floatingButtonsLayer = useRef(null);
  const chatLayer = useRef(null);
  const cardLayer = useRef(null);
  const floatingButtonsRef = useRef(null);
  const chatLayerRef = useRef(null);
  const backgroundRef = useRef(null);
  const woojuMachineRef = useRef(null);
  const ballImgRef = useRef(null);
  const lastUserMessageRef = useRef("");
  const infoCardRef = useRef(null);
  const citySelectorRef = useRef(null);
  const subtitleStreamRef = useRef(null);

  // ── Voice subtitle helpers ──

  const clearVoiceSubtitle = () => {
    if (subtitleStreamRef.current) {
      clearTimeout(subtitleStreamRef.current);
      subtitleStreamRef.current = null;
    }
    setVoiceStreamText("");
    setVoiceStreamCursor(false);
    setVoiceSubtitleFading(false);
  };

  const dissolveVoiceSubtitle = () => {
    setVoiceStreamCursor(false);
    subtitleStreamRef.current = setTimeout(() => {
      setVoiceSubtitleFading(true);
      subtitleStreamRef.current = setTimeout(() => {
        setVoiceStreamText("");
        setVoiceSubtitleFading(false);
      }, 700);
    }, 400);
  };

  const randomMachineGif = () =>
    Math.random() < 0.5 ? "/images/wooju_machine.gif" : "/images/wooju_machine_original.gif";

  // Function to clear user-specific data when switching users
  const clearUserData = () => {
    // Clear only app-specific data, preserve user profile data
    try {
      // Only clear flow state, not profile data
      localStorage.removeItem('wooju_flow_state');
      sessionStorage.clear();
    } catch (error) {
      // Silent fail
    }

    setCardString("");
    setCardReadingText("");
    setInfoCardResetKey(0);
    setCitySelectorResetKey(0);
  };

  // Function to load user data when they log in
  const loadUserData = async () => {
    console.log('loadUserData called for user:', auth.currentUser?.uid);
    // Components now load their own data from localStorage
    // This function can be used for any app-wide user data loading
  };

  // Function to auto-save birth chart data when store is updated
  const autoSaveBirthChartData = async () => {
    // Components now handle their own saving
  };

  // Function to clear user data from localStorage (optional, for privacy)
  const clearUserLocalStorageData = async () => {
    try {
      if (auth.currentUser && auth.currentUser.uid) {
        console.log('Data preservation mode: not deleting user data');
        // Note: We're preserving all user data for analytics and user experience
        // No actual deletion occurs
      }
    } catch (error) {
      console.error('Error in clearUserLocalStorageData:', error);
    }
  };

  // Function to save birth chart data to localStorage
  const saveBirthChartData = async (birthChartData) => {
    // Components now handle their own saving to localStorage
  };

  // Function to load birth chart data from localStorage
  const loadBirthChartData = async () => {
    // Components now handle their own loading from localStorage
  };

  // Authentication effect
  useEffect(() => {
    // Prevent duplicate setup
    let isSetupComplete = false;
    let timeoutId;
    let previousUserId = null;

    // Add a timeout fallback to prevent infinite loading
    timeoutId = setTimeout(() => {
      if (isSetupComplete) return; // Prevent duplicate processing
      console.log('Auth timeout - setting loading to false');
      setLoading(false);
      isSetupComplete = true;
    }, 10000); // 10 second timeout

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (isSetupComplete) return; // Prevent duplicate processing
      clearTimeout(timeoutId);

      if (user && user.uid) {
        // User is authenticated (anonymous or otherwise)
        console.log('User authenticated:', user.uid, 'Anonymous:', user.isAnonymous);
        // Only clear data if this is a different user
        if (previousUserId && previousUserId !== user.uid) {
          clearUserData(); // Clear any previous user's data
        }
        previousUserId = user.uid;
        setUser(user);
        setLoading(false);
        // Load the user's saved data
        loadUserData();
      } else {
        // No user signed in
        console.log('No user signed in');
        clearTimeout(timeoutId);
        setUser(null);
        setLoading(false);
      }
      isSetupComplete = true;
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
      isSetupComplete = true;
    };
  }, []);

  // Login function
  const handleLogin = async () => {
    if (loginInProgress) {
      return;
    }

    try {
      setLoginInProgress(true);

      // Sign in anonymously
      const result = await signInAnonymously(auth);

      // Immediately set the user state after successful anonymous authentication
      if (result.user && result.user.uid) {
        // Don't clear data on login - preserve user's saved profile
        setUser(result.user);
        setLoading(false);
        // Load the user's saved data
        loadUserData();
      }
    } catch (error) {
      console.error('Anonymous sign-in error:', error);
      // Silent fail
    } finally {
      setLoginInProgress(false);
    }
  };

  // Removed handleLogoutConfirm function - no longer needed

  // Persist a single FlowController instance across renders
  const flowControllerRef = useRef(null);
  if (!flowControllerRef.current) {
    flowControllerRef.current = new FlowController();
  }
  const flowController = flowControllerRef.current;

  const toggleVoiceMode = () => {
    const next = !voiceMode;
    setVoiceModeState(next);
    voiceModeRef.current = next;
    flowController.setVoiceMode(next);
    if (!next) {
      chatLayerRef.current?.stopSpeaking?.();
      window.speechSynthesis?.cancel();
      setWoojuSpeaking(false);
      setIsVoiceProcessing(false);
      setIsListening(false);
      clearVoiceSubtitle();
    }
  };

  // Simple function to change GIF
  const setNextAnimation = (nextSrc) => {
    if (woojuMachineRef.current) {
      woojuMachineRef.current.src = nextSrc;
    }
  };

  // Simple show/hide methods
  const show = (layer) => {
    if (layer && layer.current) {
      layer.current.style.display = layer == woojuMachineLayer ? "flex" : "block";
    }
  };

  const hide = (layer) => {
    if (layer && layer.current) {
      layer.current.style.display = "none";
    }
  };

  // Background color method
  const setBackground = (color) => {
    if (backgroundRef.current) {
      backgroundRef.current.style.backgroundImage = color;
    }
  };

  // Background position method
  const setBackgroundPosition = (position) => {
    if (backgroundRef.current) {
      backgroundRef.current.style.backgroundPosition = position;
    }
  };

  async function showScreen(nextScreen) {
    try {
      if (nextScreen.screenName == SCREENS.INFO_CARD) {
        // If we are collecting person2, optionally blank the form
        if (nextScreen.infoCard && nextScreen.infoCard.requireBlank) {
          // This is for person 2 (partner)
          setIsPartnerForm(true);
          // Clear the React state so the form renders blank
          try {
            // setBirthChartData({ // This line is removed
            //   name: "",
            //   birthDate: null,
            //   birthLocation: null,
            //   unknownTime: false,
            //   gender: "F",
            // });
            // Force remount of InfoCard to reset local input states
            setInfoCardResetKey((k) => k + 1);
          } catch (e) {
            // noop if store unavailable here
          }
        } else {
          // This is for person 1 (self)
          setIsPartnerForm(false);
        }
        show(infoCardLayer);
      } else if (nextScreen.screenName == SCREENS.CHAT) {
        if (voiceModeRef.current) {
          // ── VOICE MODE: WoojuMachine stays visible, subtitle panel shows response ──
          hide(infoCardLayer);
          hide(cardLayer);
          hide(chatLayer);
          show(floatingButtonsLayer);
          // Response arrived — clear processing state and loading GIF
          setIsVoiceProcessing(false);
          if (ballImgRef.current) ballImgRef.current.style.display = "none";
          if (nextScreen.chat.text) {
            if (subtitleStreamRef.current) clearTimeout(subtitleStreamRef.current);
            const words = nextScreen.chat.text.split(/\s+/).filter(Boolean);
            let i = 0;
            let current = "";
            setVoiceStreamText("");
            setVoiceStreamCursor(true);
            setVoiceSubtitleFading(false);

            // Mirror the ChatLayer streaming: word-by-word with natural pacing
            const showNext = () => {
              if (i < words.length) {
                const word = words[i];
                current += (i > 0 ? " " : "") + word;
                setVoiceStreamText(current);
                i++;
                const delay = 110
                  + Math.random() * 70
                  + (/[.!?,;:]/.test(word) ? 350 : 0)
                  + (/[.!?]$/.test(word) ? 500 : 0);
                subtitleStreamRef.current = setTimeout(showNext, delay);
              } else {
                subtitleStreamRef.current = null;
              }
            };
            subtitleStreamRef.current = setTimeout(showNext, 60);

            if (typeof window !== "undefined" && window.speechSynthesis) {
              window.speechSynthesis.cancel();
              const utterance = new window.SpeechSynthesisUtterance(nextScreen.chat.text);
              setWoojuSpeaking(true);
              utterance.onend = () => {
                setWoojuSpeaking(false);
                if (subtitleStreamRef.current) {
                  clearTimeout(subtitleStreamRef.current);
                  subtitleStreamRef.current = null;
                }
                dissolveVoiceSubtitle();
              };
              window.speechSynthesis.speak(utterance);
            }
          }
        } else {
          // ── TEXT MODE: existing chat-layer behaviour ──
          hide(woojuMachineLayer);
          hide(floatingButtonsLayer);
          if (floatingButtonsRef.current?.clear) {
            await floatingButtonsRef.current.clear();
          }
          setNextAnimation("/images/hand_wave.gif");
          if (chatLayerRef.current?.clearText) {
            chatLayerRef.current.clearText();
          }
          show(chatLayer);
          chatLayerRef.current.setOverlayBackground(false);
          chatLayer.current.style.animation = "flyUpAndIn 0.4s";
          await sleep(0.4);
          chatLayer.current.style.animation = "none";
          chatLayerRef.current.setCompactText(false);
          chatLayerRef.current.setAlignTop(false);
          chatLayerRef.current.displayText(nextScreen.chat.text);
          chatLayerRef.current.setAllowTextInput(nextScreen.chat.allowResponse);
          if (nextScreen.chat.allowResponse) {
            (async () => {
              try {
                const fallbackPrompts = [
                  "What is a Day Pillar in Saju?",
                  "Could this be a pattern in my relationships?",
                  "What should I work on based on this insight?"
                ];
                if (chatLayerRef.current?.setSuggestedPrompts) {
                  chatLayerRef.current.setSuggestedPrompts(fallbackPrompts);
                }
                const gen = httpsCallable(functions, "generateSuggestedPrompts");
                const res = await gen({
                  userQuestion: lastUserMessageRef.current || "hi",
                  aiResponse: nextScreen.chat.text || "",
                  isRedFlagAnalysis: flowController.mode === "self",
                  isKorean: false,
                });
                const prompts = (res.data && res.data.suggestedPrompts) || fallbackPrompts;
                if (chatLayerRef.current?.setSuggestedPrompts) {
                  chatLayerRef.current.setSuggestedPrompts(prompts);
                }
              } catch (error) {
                console.log("Using fallback prompts due to error:", error);
              }
            })();
          } else if (chatLayerRef.current?.clearSuggestedPrompts) {
            chatLayerRef.current.clearSuggestedPrompts();
          }
        }
      } else if (nextScreen.screenName == SCREENS.CARD_READING) {
        setNextAnimation("/images/card_throw.gif");
        await sleep(5);
        setNextAnimation(randomMachineGif());
        hide(woojuMachineLayer);
        // Show card first, keep chat hidden initially
        hide(chatLayer);
        show(cardLayer);
        setCardString(nextScreen.cardReading.card);
        setCardReadingText(nextScreen.cardReading.text || "");
        // Wait for user to fold the card via X, then reveal text and chat
      } else if (nextScreen.screenName == SCREENS.WOOJU_MACHINE) {
        const bubble = nextScreen?.woojuMachine?.bubbleText || "";
        const isAskMeAnythingMain =
          typeof bubble === "string" && bubble.includes("Ask me anything—Wooju's listening");
        const isTopicsPrompt =
          typeof bubble === "string" && bubble.includes("what's on your mind today?");
        const reachedNow = hasReachedTopics || isTopicsPrompt;
        if (reachedNow && !hasReachedTopics) {
          setHasReachedTopics(true);
        }
        // Persist visibility once reached; never hide again
        if (!isAskMeAnythingMain && (reachedNow || hasReachedTopics)) {
          setShowNewReadingButton(true);
        }
        hide(infoCardLayer);
        hide(chatLayer);
        hide(cardLayer);
        show(woojuMachineLayer);
        show(floatingButtonsLayer);
        clearVoiceSubtitle();

        // Show hand_wave.gif for topic selection, random machine for other states
        if (nextScreen.woojuMachine.bubbleText === "what's on your mind today?") {
          setNextAnimation("/images/hand_wave.gif");
        } else {
          setNextAnimation(randomMachineGif());
        }

        if (floatingButtonsRef.current) {
          await floatingButtonsRef.current.setButtonsAndSpeechBubble(
            nextScreen.woojuMachine.floatingButtons,
            nextScreen.woojuMachine.bubbleText,
            { large: !!nextScreen.woojuMachine.largeSpeechBubble }
          );
        }
      }
    } catch (error) {
      // Silent fail
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const showScreenCallback = useCallback(showScreen, []); // Empty dependency array to make it stable

  // Set initial background color and layer visibility
  const isInitialized = useRef(false);

  useEffect(() => {
    // Only initialize the app flow after user authentication
    if (isInitialized.current || !user) {
      return;
    }
    isInitialized.current = true;

    // Set the initial background
    setBackground(
      "radial-gradient(circle at 0% 50%, #de8aef 0%, #ac8aeb 30%, #8a5a9f 60%, #0a0a14 100%)"
    );
    setBackgroundPosition("20% 50%");

    (async () => {
      // Check if user has saved birth chart data in localStorage
      try {
        const savedProfileData = localStorage.getItem('wooju_profile_data');

        if (savedProfileData) {
          const parsedData = JSON.parse(savedProfileData);
          // If we have saved profile data, show the info card layer
          if (parsedData.name && parsedData.birthDate) {
            show(infoCardLayer);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking localStorage for saved data:', error);
      }

      // If no saved data, show the normal flow
      await showScreenCallback(
        await flowController.getNextScreen({
          interactionType: INTERACTION_TYPES.OPEN_APP,
        })
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Now depends on user authentication

  // Animation for completing InfoCard
  const AnimateInfoCardComplete = async (formData) => {
    try {
      infoCardLayer.current?.classList.add("info-card-falling");
      await sleep(3);
      infoCardLayer.current?.classList.remove("info-card-falling");

      // Show machine layer and set shimmer ball animation BEFORE card starts disappearing
      show(woojuMachineLayer);
      console.log('Machine layer shown, setting shimmer ball...');
      setNextAnimation("/images/ball_shimmer.gif");

      // Small delay to ensure shimmer ball is visible
      await sleep(0.5);

      infoCardLayer.current?.classList.add("info-card-disappearing");
      await sleep(3);
      infoCardLayer.current?.classList.remove("info-card-disappearing");
      hide(infoCardLayer);
      await sleep(2);
      setNextAnimation(randomMachineGif());
      const nextScreen = await flowController.getNextScreen({
        interactionType: INTERACTION_TYPES.INFO_CARD_SUBMIT,
        data: formData,
      });
      console.log('Next screen returned:', nextScreen.screenName);
      await showScreenCallback(nextScreen);
    } catch (error) {
      // Silent fail
      throw error;
    }
  };

  // Animation for showing CitySelector
  const AnimateCitySelectorShow = async (updateCity) => {
    // Ensure CitySelector remounts fresh (resets map/pan state) on every open
    setCitySelectorResetKey((k) => k + 1);
    hide(woojuMachineLayer);
    // Animate to darker area of the radial gradient
    setBackgroundPosition("100% 90%");

    // Add animation classes
    infoCardLayer.current?.classList.add("card-panning");

    // Wait for animation
    await sleep(1.5);

    // Remove animation classes and set final state
    infoCardLayer.current?.classList.remove("card-panning");
    hide(infoCardLayer);

    show(citySelectorLayer);
    citySelectorLayer.current?.classList.add("city-appearing");

    // Wait for city selector animation
    await sleep(1.5);

    // Remove animation classes
    citySelectorLayer.current?.classList.remove("city-appearing");
  };

  // Animation for hiding CitySelector
  const AnimateCitySelectorDone = async () => {
    // Animate back to the info card
    hide(citySelectorLayer);
    show(infoCardLayer);

    // Animate back to the original background position
    setBackgroundPosition("50% 50%");

    // Add animation classes
    infoCardLayer.current?.classList.add("card-returning");

    // Wait for animation
    await sleep(1.5);

    // Remove animation classes
    infoCardLayer.current?.classList.remove("card-returning");
  };

  // Handle floating button clicks (also handles raw voice transcripts in voice mode)
  const handleFloatingButtonClick = async (buttonText) => {
    const resolvedText = voiceMode
      ? classifyVoiceIntent(buttonText, flowController.currentInteraction, flowController.mode)
      : buttonText;

    if (floatingButtonsRef.current?.clear) {
      await floatingButtonsRef.current.clear();
    }

    // Voice mode: show processing state on orb + loading GIF on machine
    if (voiceModeRef.current) {
      setIsVoiceProcessing(true);
      clearVoiceSubtitle();
      const loadingAnimations = ["/images/ball_bounce.gif", "/images/wooju_magic_wave.gif", "/images/light_flash.gif", "/images/ball_shimmer.gif", "/images/ball_swirl.gif"];
      const randomAnim = loadingAnimations[Math.floor(Math.random() * loadingAnimations.length)];
      if (ballImgRef.current) {
        ballImgRef.current.src = randomAnim;
        ballImgRef.current.style.display = "block";
      }
    }

    try {
      await showScreenCallback(
        await flowController.getNextScreen({
          interactionType: INTERACTION_TYPES.FLOATING_BUTTONS,
          data: resolvedText,
        })
      );
    } catch (err) {
      console.error("handleFloatingButtonClick error:", err);
      setIsVoiceProcessing(false);
      if (ballImgRef.current) ballImgRef.current.style.display = "none";
      if (voiceModeRef.current && window.speechSynthesis) {
        setWoojuSpeaking(true);
        const errUtterance = new window.SpeechSynthesisUtterance(
          "Sorry, I had trouble connecting. Give me a moment and try again."
        );
        errUtterance.onend = () => setWoojuSpeaking(false);
        window.speechSynthesis.speak(errUtterance);
      }
    }
  };

  // Handle chat user input
  const handleChatUserInput = async (userInput) => {
    lastUserMessageRef.current = userInput;
    hide(chatLayer);

    show(woojuMachineLayer);
    setNextAnimation(randomMachineGif());

    const loadingAnimations = ["/images/ball_bounce.gif", "/images/wooju_magic_wave.gif", "/images/light_flash.gif", "/images/ball_shimmer.gif", "/images/ball_swirl.gif"];
    const randomAnimation = loadingAnimations[Math.floor(Math.random() * loadingAnimations.length)];

    if (ballImgRef.current) {
      ballImgRef.current.src = randomAnimation;
      ballImgRef.current.style.display = "block";
    }

    if (woojuMachineRef.current) {
      woojuMachineRef.current.style.filter = "brightness(1)";
      if (woojuMachineRef.current.parentElement) {
        woojuMachineRef.current.parentElement.style.transform = "scale(1)";
      }
    }

    const nextScreen = await flowController.getNextScreen({
      interactionType: INTERACTION_TYPES.CHAT_CONTINUE,
      data: userInput,
    });

    // Hide loading animation before showing response
    if (ballImgRef.current) {
      ballImgRef.current.style.display = "none";
    }

    await showScreenCallback(nextScreen);
  };

  // Function to handle new reading
  const handleNewReading = async () => {
    if (logoutInProgress) {
      return;
    }
    setLogoutInProgress(true);
    // Removed - no longer needed with direct New Reading button

    clearVoiceSubtitle();
    setWoojuSpeaking(false);
    setIsVoiceProcessing(false);
    setCardString("");
    setCardReadingText("");
    setInfoCardResetKey(0);
    setCitySelectorResetKey(0);

    // Reset the flow controller to start fresh
    if (flowControllerRef.current) {
      flowControllerRef.current.reset();
    }

    // Show the birth chart form
    hide(woojuMachineLayer);
    hide(chatLayer);
    hide(cardLayer);
    show(infoCardLayer);

    setLogoutInProgress(false);
  };


  // Show login screen if not authenticated
  if (!user) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#FFF6EC',
        color: '#333'
      }}>
        <div style={{
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.1)',
          padding: '40px',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
            <img
              src="/images/tiger_logo_240.png"
              srcSet="/images/tiger_logo_240.png 1x, /images/tiger_logo_480.png 2x"
              alt="Tiger Logo"
              loading="eager"
              fetchpriority="high"
              style={{
                width: '280px',
                height: '280px',
                objectFit: 'contain'
              }}
            />
          </div>
          <button
            onClick={handleLogin}
            disabled={loginInProgress}
            style={{
              background: loginInProgress ? '#ccc' : '#84D1C7',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              borderRadius: '8px',
              fontSize: '20px',
              fontWeight: '500',
              cursor: loginInProgress ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto'
            }}
          >
            {loginInProgress ? 'Starting...' : 'would you wooju?'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={backgroundRef}
      className="App"
      style={{
        transition: "background 3s ease-in-out",
        backgroundSize: "1000% 1000%",
      }}
    >
      <div className="app-container">
        {/* New Reading Button */}
        {showNewReadingButton && (
          <button
            onClick={handleNewReading}
            disabled={logoutInProgress}
            className="logout-button-mobile"
            title="Start a new reading with your current profile"
          >
            New Reading
          </button>
        )}

        {/* Voice Mode Toggle — only shown after birth form is submitted */}
        {hasReachedTopics && <button
          onClick={toggleVoiceMode}
          title={voiceMode ? "Switch to text chat" : "Switch to voice chat"}
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 10002,
            background: voiceMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 255, 255, 0.25)",
            color: "#fbf2ea",
            border: voiceMode ? "1px solid rgba(255, 255, 255, 0.4)" : "1px solid #0ff",
            padding: "8px 14px",
            borderRadius: 12,
            fontSize: "0.85rem",
            fontFamily: "'Nanum Myeongjo', 'Gowun Batang', serif",
          }}
        >
          {voiceMode ? "💬 Text" : "🎙️ Voice"}
        </button>}

        {/* Delete Data Confirmation Popup */}
        {/* Removed - no longer needed with direct New Reading button */}

        {/* WoojuMachine Layer */}
        <div
          ref={woojuMachineLayer}
          className="layer wooju-machine-layer"
          style={{
            padding: 10,
            paddingTop: "calc(var(--safe-area-top) + 48px)",
            alignItems: "center",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              flex: 1,
              position: "relative",
              transformOrigin: "49.9% 62.2%", // this is the center of the ball.
            }}
          >
            <img
              ref={woojuMachineRef}
              src="/images/wooju_machine.gif"
              alt="Wooju Machine"
              style={{
                width: "100%",
              }}
            />
            <img
              ref={ballImgRef}
              src="/images/ball_outline.png"
              alt="Ball"
              style={{
                width: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                display: "none",
              }}
            />
          </div>
        </div>

        {/* InfoCard Layer */}
        <div
          ref={infoCardLayer}
          className="layer info-card-layer"
          style={{
            display: "none",
            paddingTop: "80px" // Position below the logout button
          }}
        >
          <div className="info-card-container">
            <InfoCard
              key={infoCardResetKey}
              ref={infoCardRef}
              isPartnerForm={isPartnerForm}
              onContinue={async (formData) => {
                try {
                  await AnimateInfoCardComplete(formData);
                  // Switch to wooju machine screen
                } catch (error) {
                  // Silent fail
                }
              }}
              onShowCitySelector={() => AnimateCitySelectorShow()}
            />
          </div>
        </div>

        {/* CitySelector Layer */}
        <div
          ref={citySelectorLayer}
          className="layer city-selector-layer"
          style={{ display: "none" }}
        >
          <CitySelector
            key={citySelectorResetKey}
            isPartnerForm={isPartnerForm}
            onDone={AnimateCitySelectorDone}
            updateCity={(city) => infoCardRef.current?.updateCity(city)}
          />
        </div>

        {/* FloatingButtons Layer */}
        <div
          ref={floatingButtonsLayer}
          className="layer floating-buttons-layer"
          style={{ display: "none" }}
        >
          <FloatingButtons
            ref={floatingButtonsRef}
            voiceMode={voiceMode}
            woojuSpeaking={woojuSpeaking}
            isProcessing={isVoiceProcessing}
            onListeningChange={setIsListening}
            onButtonClick={handleFloatingButtonClick}
            onTextSubmit={async (text) => {
              // Clear floating UI before transitioning to chat
              if (floatingButtonsRef.current?.clear) {
                await floatingButtonsRef.current.clear();
              }

              show(woojuMachineLayer);
              setNextAnimation(randomMachineGif());

              const loadingAnimations = ["/images/ball_bounce.gif", "/images/wooju_magic_wave.gif", "/images/light_flash.gif", "/images/ball_shimmer.gif", "/images/ball_swirl.gif"];
              const randomAnimation = loadingAnimations[Math.floor(Math.random() * loadingAnimations.length)];

              // Show random loading animation while waiting for response
              if (ballImgRef.current) {
                ballImgRef.current.src = randomAnimation;
                ballImgRef.current.style.display = "block";
              }

              if (woojuMachineRef.current) {
                woojuMachineRef.current.style.filter = "brightness(1)";
                if (woojuMachineRef.current.parentElement) {
                  woojuMachineRef.current.parentElement.style.transform = "scale(1)";
                }
              }

              // Get the next screen (this will take time for Firebase calls)
              const nextScreen = await flowController.getNextScreen({
                interactionType: INTERACTION_TYPES.CHAT_CONTINUE,
                data: text,
              });

              // Hide loading animation before showing response
              if (ballImgRef.current) {
                ballImgRef.current.style.display = "none";
              }

              await showScreenCallback(nextScreen);
            }}
          />
        </div>

        {/* ChatLayer */}
        <div
          ref={chatLayer}
          className="layer chat-layer"
          style={{ display: "none" }}
        >
          <ChatLayer ref={chatLayerRef} onUserInput={handleChatUserInput} voiceMode={voiceMode} />
        </div>

        {/* CardLayer */}
        <div
          ref={cardLayer}
          className="layer card-layer"
          style={{ display: "none", pointerEvents: "none" }}
        >
          <CardLayer
            cardString={cardString}
            onFold={async () => {
              // After folding the card, show chat with the generated text above the minimized card
              // Also restore the wooju machine layer so it's visible behind the chat
              show(woojuMachineLayer);
              show(chatLayer);
              chatLayerRef.current.setOverlayBackground(false);
              chatLayerRef.current.setCompactText(true);
              chatLayerRef.current.setAlignTop(true);
              if (cardReadingText) {
                chatLayerRef.current.displayText(cardReadingText);
                if (voiceMode && typeof window !== "undefined" && window.speechSynthesis) {
                  window.speechSynthesis.cancel();
                  window.speechSynthesis.speak(new window.SpeechSynthesisUtterance(cardReadingText));
                }
              }
            }}
          />
        </div>

        {/* Voice subtitle — streaming word-by-word with cursor, lower screen */}
        {voiceMode && voiceStreamText && (
          <div
            style={{
              position: "fixed",
              bottom: "calc(var(--safe-area-bottom) + 130px)",
              left: "20px",
              right: "20px",
              padding: "14px 22px",
              background: "rgba(0,0,0,0.42)",
              borderRadius: "16px",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              color: "rgba(255,255,255,0.95)",
              fontSize: "1.05rem",
              lineHeight: 1.6,
              fontFamily: "'Nanum Myeongjo', 'Gowun Batang', serif",
              textAlign: "center",
              zIndex: 998,
              pointerEvents: "none",
              opacity: voiceSubtitleFading ? 0 : 1,
              transition: voiceSubtitleFading ? "opacity 0.7s ease-in" : "opacity 0.15s ease-out",
            }}
          >
            {voiceStreamText}
            {voiceStreamCursor && (
              <span
                style={{
                  display: "inline-block",
                  width: "2px",
                  height: "1.1em",
                  backgroundColor: "rgba(255,255,255,0.85)",
                  marginLeft: "3px",
                  animation: "voiceCursorBlink 1s infinite",
                  verticalAlign: "text-bottom",
                  borderRadius: "1px",
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
